<template>
  <a-config-provider :locale="pickerLocale">
    <div
      class="my-5 base_picker_wrapper"
      :class="[
        col ? `col-lg-${col}` : '',
        isRtl ? 'is-rtl' : 'is-ltr',
      ]"
      lang="en"
    >
      <!-- ========== Start:: Date Picker Input ========== -->
      <div class="input_wrapper top_label" v-if="type === 'date'">
        <label class="form-label">
          {{ placeholder }}
          <span class="text-danger" v-if="required">*</span>
        </label>
        <a-date-picker
          class="base_picker_input"
          placeholder=""
          format="YYYY-MM-DD"
          valueFormat="YYYY-MM-DD"
          :value="value"
          :disabled-date="disabledDate"
          @change="updateValue($event)"
          :readonly="readonly"
          :disabled="disabled"
        />
      </div>
      <!-- ========== End:: Date Picker Input ========== -->
      <!-- ========== Start:: Time Picker Input ========== -->
      <div class="input_wrapper top_label" v-if="type === 'time'">
        <label class="form-label">
          {{ placeholder }}
          <span class="text-danger" v-if="required">*</span>
        </label>
        <a-time-picker
          class="base_picker_input w-100"
          placeholder=""
          format="HH:mm"
          valueFormat="HH:mm"
          :use12Hours="false"
          :value="value"
          @change="updateValue($event)"
          :readonly="readonly"
          :disabled="disabled"
        />
      </div>
      <!-- ========== End:: Time Picker Input ========== -->
    </div>
  </a-config-provider>
</template>

<script>
import { mapGetters } from "vuex";
import enUS from "ant-design-vue/es/locale/default";

function toLatinDigits(value) {
  return String(value ?? "")
    .replace(/[\u0660-\u0669]/g, (digit) =>
      String(digit.charCodeAt(0) - 0x0660)
    )
    .replace(/[\u06F0-\u06F9]/g, (digit) =>
      String(digit.charCodeAt(0) - 0x06f0)
    );
}

export default {
  name: "BasePickerInput",

  data() {
    return {
      pickerLocale: enUS,
    };
  },

  props: {
    col: {
      required: false,
      type: String,
    },
    value: {
      required: true,
    },
    disabledDate: {
      required: false,
    },
    type: {
      required: true,
      type: String,
      validator: (value) => ["date", "time"].includes(value),
    },
    placeholder: {
      required: false,
      type: String,
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    readonly: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters("AppLangModule", ["getAppLocale"]),
    isRtl() {
      return (this.getAppLocale || this.$i18n?.locale) === "ar";
    },
  },

  methods: {
    normalizePickerValue(value) {
      if (value === null || value === undefined || value === "") {
        return value;
      }

      return typeof value === "string" ? toLatinDigits(value) : value;
    },
    updateValue(value) {
      this.$emit("input", this.normalizePickerValue(value));
    },
  },
};
</script>

<style scoped>
.base_picker_wrapper {
  width: 100%;
}

.base_picker_input {
  width: 100%;
  display: block;
}

.base_picker_input >>> .ant-calendar-picker,
.base_picker_input >>> .ant-time-picker {
  width: 100%;
  display: block;
}

/* Values stay LTR (YYYY-MM-DD / HH:mm) for backend compatibility */
.base_picker_input >>> .ant-calendar-picker-input,
.base_picker_input >>> .ant-time-picker-input {
  direction: ltr;
  unicode-bidi: isolate;
  width: 100%;
}

.base_picker_wrapper.is-ltr .base_picker_input >>> .ant-calendar-picker-input,
.base_picker_wrapper.is-ltr .base_picker_input >>> .ant-time-picker-input {
  text-align: left;
  padding: 12px 12px 12px 40px;
}

.base_picker_wrapper.is-rtl .base_picker_input >>> .ant-calendar-picker-input,
.base_picker_wrapper.is-rtl .base_picker_input >>> .ant-time-picker-input {
  text-align: right;
  padding: 12px 40px 12px 12px;
}

.base_picker_input >>> .ant-calendar-picker-icon,
.base_picker_input >>> .ant-time-picker-icon {
  left: 12px;
  right: auto;
}
</style>
